import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Button, Spinner } from "reactstrap";
import commonStyles from "../../../../../src/assets/scss/common/common.module.scss";
import { workflowStatusColumns } from "../../../../helpers/constant";
import { useNavigate } from "react-router-dom";
import { LIBRARY_PATHS } from "../../../../Routes/paths";
import DeleteModal from "Components/Common/DeleteModal";
import {
  useSelectAllWorkflowData,
  useDeleteWorkflowData,
  useUpdateWorkflowData,
} from "modules/Library/Workflow/api/useWorkflowQuery";
import { perPageData, showToastMessage } from "common/service/CommonService";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { useQueryClient } from "@tanstack/react-query";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import Reason from "common/utilities/Reason/Reason";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import { usePermission } from "../../../../context/PermissionContext";
import { Workflow } from "../helpers/workflow.types";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";

/* ----------------------------------
   Component
---------------------------------- */

const WorkflowStatusList: React.FC = () => {
  document.title = "Workflow | ZENTIXS VISTA"
  const { hasPermission } = usePermission();
  const canAssign = hasPermission(
    PERMISSION_MODULES.WORKFLOW,
    PERMISSION_ACTIONS.ASSIGN_TO_MODULE,
  );

  // Query client
  const queryClient = useQueryClient();
  const [workflowData, setWorkflowData] = useState<Workflow[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null,
  );
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showReasonModal, setShowReasonModal] = useState(false);

  const [selectedStudyId, setSelectedStudyId] = useState<number | null>(null);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<number | null>(
    null,
  );

  const { selectAllQuery } = useSelectAllWorkflowData(
    searchText,
    currentPage,
    perPageData,
  );
  const { data: data, isLoading } = selectAllQuery;

  const { deleteWorkflowData } = useDeleteWorkflowData();
  const { updateWorkflowData } = useUpdateWorkflowData();
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const ToggleDeleteReason = (): void => setShowDeleteReason((prev) => !prev);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const toggleDeleteModal = (): void => setDeleteModal(!deleteModal);

  /* ---------- LOAD WORKFLOW LIST ---------- */
  useEffect(() => {
    const list = data?.data ?? [];
    setWorkflowData(
      list.map((item: Workflow) => ({
        ...item,
      })),
    );
    const defaultStudy = list.find(
      (item: any) => item.attachedmodule === "study",
    );

    if (!selectAllQuery.isFetching) {
      if (defaultStudy) setSelectedStudyId(defaultStudy.workflowid);
    }
  }, [data, selectAllQuery.isFetching]); // Re-run when fetching finishes

  /* ---------- ADD NEW WORKFLOW ---------- */
  const navigateToAdd = (): void => {
    sessionStorage.removeItem("currentWorkflow");
    navigate(LIBRARY_PATHS.WORKFLOW_ADD);
  };

  /* ---------- EDIT WORKFLOW ---------- */
  const handleEdit = (row: Workflow): void => {
    navigate(`/workflow/edit/${row.workflowid}`);
  };

  /* ---------- SEARCH WORKFLOW ---------- */
  const onSearch = (text: string | null) => {
    if (text === searchText) return;
    setSearchText(text);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  /* ---------- DELETE ---------- */
  const onDeleteClick = (row: Workflow): void => {
    setSelectedWorkflow(row);
    setDeleteModal(true);
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }): void => {
    if (!selectedWorkflow) return;
    const payload = {
      id: reasonPayload.id,
      updatereason: reasonPayload.reason,
    };
    deleteWorkflowData.mutate(payload, {
      onSuccess: (response) => {
        const totalRecords = data?.data[0]?.total_count || 0;
        const updatedTotalRecords = totalRecords - 1;
        const lastPage = Math.ceil(updatedTotalRecords / perPageData);
        const updatedPage =
          lastPage === 0 ? 1 : Math.min(currentPage, lastPage);
        setCurrentPage(updatedPage);
        queryClient.invalidateQueries({ queryKey: ["workflow-selectall"] });
        showToastMessage("success", response?.message);
        toggleDeleteModal();
        setSelectedWorkflow(null);
        setSearchText("");
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || error);
      },
    });
  };

  /* ---------- VIEW ---------- */
  const handleView = (row: Workflow) => {
    navigate(`/workflow/view/${row.workflowid}`);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleToggleClick = (row: any, type: "study" | "pharmacy") => {
    const currentId = type === "study" ? selectedStudyId : selectedPharmacyId;

    // If already active, do nothing (can't deselect without backend support)
    if (currentId === row.workflowid) return;

    setSelectedRow({ ...row, assignType: type });
    setShowReasonModal(true);
  };

  //  to assign a module to a particular workflow
  const handleConfirmAssign = (payload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRow) return;

    const assignType = selectedRow.assignType as "study" | "pharmacy";

    const dataPayload = {
      workflowid: selectedRow.workflowid,
      name: selectedRow.name,
      updatereason: payload.reason,
      attachedmodule: assignType === "study" ? "study" : "pharmacy",
    };

    updateWorkflowData.mutate(dataPayload, {
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: ["workflow-selectall"] });
        showToastMessage("success", res?.message);

        // Update the correct state based on type
        if (assignType === "study") {
          setSelectedStudyId(selectedRow.workflowid);
        } else {
          setSelectedPharmacyId(selectedRow.workflowid);
        }

        setShowReasonModal(false);
        setSelectedRow(null);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err);
      },
    });
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Workflow",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Workflow_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage(
          "error",
          error?.message || "Error fetching audit logs",
        );
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const getDynamicColumns = (row: Workflow) => {
    const isStudy = row.attachedmodule?.toLowerCase() === "study";
    return {
      onView: handleView,
      ...(!isStudy && {
        onDelete: onDeleteClick,
      }),
      onEdit: handleEdit,
    };
  };

  /* ---------- RENDER ---------- */
  return (
    <>
      <div className="page-content">
        <Container fluid className="px-0">
          <Card className={commonStyles.card}>
            <CardBody>
              <div className="row g-4 mb-3">
                <div className="col-sm-auto">
                  <CommonSearch onSearch={onSearch} searchText={searchText} />
                </div>
                <div className="col-sm">
                  <div className="d-flex justify-content-sm-end gap-2">
                    <CommonButton
                      color="primary"
                      size="sm"
                      onClick={onAuditClick}
                      disabled={AuditMutation.isPending}
                      className={commonStyles.btn_small}
                      module={PERMISSION_MODULES.WORKFLOW}
                      action={PERMISSION_ACTIONS.AUDIT}
                      outline
                    >
                      {AuditMutation.isPending ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <i className="ri-history-line me-1"></i>
                      )}
                      Audit
                    </CommonButton>
                    <CommonButton
                      color="primary"
                      onClick={navigateToAdd}
                      className={commonStyles.btn_small}
                      outline
                      size="sm"
                      module={PERMISSION_MODULES.WORKFLOW}
                      action={PERMISSION_ACTIONS.ADD}
                    >
                      <i className="ri-add-line"></i> Add
                    </CommonButton>
                  </div>
                </div>
              </div>
              {/* WORKFLOW LIST */}
              <CommonTableIndexWise
                moduleName={PERMISSION_MODULES.WORKFLOW}
                columns={workflowStatusColumns(
                  canAssign,
                  selectedStudyId,
                  selectedPharmacyId,
                  handleToggleClick,
                )}
                data={workflowData}
                actions={getDynamicColumns}
                emptyText="No workflow found"
                totalRecords={workflowData[0]?.total_count || 0}
                pageSize={perPageData}
                currentPage={currentPage}
                onPageChange={onPageChange}
                loading={isLoading || selectAllQuery.isFetching}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Delete Workflow Modal */}
      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={toggleDeleteModal}
          recordId={selectedWorkflow?.workflowid ?? 0}
          recordName={selectedWorkflow?.name}
          onDeleteClick={confirmDelete}
          isLoading={deleteWorkflowData?.isPending}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={ToggleDeleteReason}
        />
      )}

      {/* Assign Reason Modal */}
      <Reason
        isOpen={showReasonModal}
        toggle={() => setShowReasonModal(false)}
        action="Assign"
        id={selectedRow?.workflowid || null}
        click={handleConfirmAssign}
        isLoading={updateWorkflowData.isPending}
      />

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </>
  );
};

export default WorkflowStatusList;
